import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Pagination,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  DateRange as DateRangeIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import * as invoiceService from '../../services/invoiceService';
import { branchesApi } from '../../services/api';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import * as XLSX from 'xlsx';

// نوع بيانات الفرع
interface BranchOption {
  _id: string;
  name: string;
}

// أنواع البيانات
interface SalesReportData {
  totalSales: number;
  totalReturns: number;
  netSales: number;
  salesCount: number;
  returnsCount: number;
  salesInvoices: InvoiceReportItem[];
  returnInvoices: InvoiceReportItem[];
}

interface InvoiceReportItem {
  _id: string;
  invoiceNumber: string;
  date: string;
  customerName?: string;
  supplierName?: string;
  total: number;
  status: string;
  type: string;
  branchName?: string;
  branchId?: string;
  pageId?: string; // إضافة معرف الصفحة
}

const SalesReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { activeBranch } = useAuth();

  // حالات البيانات
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>(['all']);
  const [reportData, setReportData] = useState<SalesReportData>({
    totalSales: 0,
    totalReturns: 0,
    netSales: 0,
    salesCount: 0,
    returnsCount: 0,
    salesInvoices: [],
    returnInvoices: []
  });

  // حالات الفلترة والصفحات - تعيين تاريخ اليوم بشكل افتراضي
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [reportType, setReportType] = useState<'all' | 'sales' | 'returns'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);

  // تحميل قائمة الفروع
  const fetchBranches = async () => {
    try {
      const response = await branchesApi.getBranches({});
      setBranches(response.branches || []);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  // تحميل بيانات التقارير
  const fetchSalesReportData = async () => {
    // التحقق من وجود فروع محددة
    if (selectedBranches.length === 0 || (selectedBranches.length === 1 && selectedBranches[0] === '')) {
      setError('يجب تحديد فرع واحد على الأقل لعرض التقارير');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // جلب فواتير المبيعات
      const salesResponse = await invoiceService.getInvoicesByType('sales_invoice', {
        startDate,
        endDate,
        branchIds: selectedBranches,
        status: 'completed'
      });

      // جلب فواتير المردودات (sales_return) - جميع الحالات بما فيها المسودات
      const salesReturnsResponse = await invoiceService.getInvoicesByType('sales_return', {
        startDate,
        endDate,
        branchIds: selectedBranches
        // إزالة فلتر الحالة لعرض جميع المردودات بما فيها المسودات
      });

      // جلب فواتير مردودات الاستيراد (import_invoice_return) - جميع الحالات بما فيها المسودات
      const importReturnsResponse = await invoiceService.getInvoicesByType('import_invoice_return', {
        startDate,
        endDate,
        branchIds: selectedBranches
        // إزالة فلتر الحالة لعرض جميع المردودات بما فيها المسودات
      });

      // تحويل البيانات إلى التنسيق المطلوب
      const salesInvoices: InvoiceReportItem[] = salesResponse.invoices.map(invoice => ({
        _id: invoice._id || '',
        invoiceNumber: invoice.invoiceNumber,
        date: invoice.date,
        customerName: invoice.customerName,
        supplierName: invoice.supplierName,
        total: invoice.total,
        status: invoice.status,
        type: 'sales_invoice',
        branchName: invoice.branchName || 'غير محدد',
        branchId: invoice.branchId || '',
        // التعامل مع pageId سواء كان نص أو كائن
        pageId: typeof invoice.pageId === 'object' && (invoice.pageId as any)?._id
          ? (invoice.pageId as any)._id
          : (invoice.pageId as string || '')
      }));

      // دمج جميع أنواع المردودات
      const allReturns = [
        ...salesReturnsResponse.invoices.map(invoice => ({
          ...invoice,
          type: 'sales_return'
        })),
        ...importReturnsResponse.invoices.map(invoice => ({
          ...invoice,
          type: 'import_invoice_return'
        }))
      ];

      const returnInvoices: InvoiceReportItem[] = allReturns.map(invoice => ({
        _id: invoice._id || '',
        invoiceNumber: invoice.invoiceNumber,
        date: invoice.date,
        customerName: invoice.customerName,
        supplierName: invoice.supplierName,
        total: invoice.total,
        status: invoice.status,
        type: invoice.type,
        branchName: invoice.branchName || 'غير محدد',
        branchId: invoice.branchId || '',
        // التعامل مع pageId سواء كان نص أو كائن
        pageId: typeof invoice.pageId === 'object' && (invoice.pageId as any)?._id
          ? (invoice.pageId as any)._id
          : (invoice.pageId as string || '')
      }));

      // حساب الإجماليات
      const totalSales = salesInvoices.reduce((sum, invoice) => sum + invoice.total, 0);
      const totalReturns = returnInvoices.reduce((sum, invoice) => sum + invoice.total, 0);
      const netSales = totalSales - totalReturns;

      setReportData({
        totalSales,
        totalReturns,
        netSales,
        salesCount: salesInvoices.length,
        returnsCount: returnInvoices.length,
        salesInvoices,
        returnInvoices
      });

    } catch (err: any) {
      console.error('Error fetching sales report data:', err);
      setError('حدث خطأ أثناء تحميل بيانات التقارير');
    } finally {
      setLoading(false);
    }
  };

  // تحميل البيانات عند تحميل المكون
  useEffect(() => {
    fetchBranches();
  }, []);

  // تحميل البيانات عند تغيير الفلاتر
  useEffect(() => {
    if (branches.length > 0) {
      fetchSalesReportData();
    }
  }, [startDate, endDate, selectedBranches, branches]);

  // معالج تحديث البيانات
  const handleRefresh = () => {
    fetchSalesReportData();
  };

  // معالج تصدير البيانات إلى Excel
  const handleExport = async () => {
    if (exporting) return;

    setExporting(true);
    setError(null);

    try {
      // جلب بيانات المبيعات
      const salesResponse = await invoiceService.getInvoicesByType('sales_invoice', {
        startDate,
        endDate,
        branchIds: selectedBranches.includes('all') ? [] : selectedBranches,
        status: 'completed'
      });

      // جلب بيانات المردودات
      const salesReturnsResponse = await invoiceService.getInvoicesByType('sales_return', {
        startDate,
        endDate,
        branchIds: selectedBranches.includes('all') ? [] : selectedBranches
      });

      const importReturnsResponse = await invoiceService.getInvoicesByType('import_invoice_return', {
        startDate,
        endDate,
        branchIds: selectedBranches.includes('all') ? [] : selectedBranches
      });

      // تحضير بيانات المبيعات للتصدير
      const salesExportData = salesResponse.invoices.map((invoice) => ({
        'رقم الفاتورة': invoice.invoiceNumber,
        'التاريخ': formatDate(invoice.date),
        'العميل': invoice.customerName || '-',
        'المورد': invoice.supplierName || '-',
        'الفرع': invoice.branchName || '-',
        'النوع': 'مبيعات',
        'الحالة': invoice.status,
        'الإجمالي': invoice.total
      }));

      // تحضير بيانات المردودات للتصدير
      const returnsExportData = [
        ...salesReturnsResponse.invoices.map((invoice) => ({
          'رقم الفاتورة': invoice.invoiceNumber,
          'التاريخ': formatDate(invoice.date),
          'العميل': invoice.customerName || '-',
          'المورد': invoice.supplierName || '-',
          'الفرع': invoice.branchName || '-',
          'النوع': 'مردودات مبيعات',
          'الحالة': invoice.status,
          'الإجمالي': invoice.total
        })),
        ...importReturnsResponse.invoices.map((invoice) => ({
          'رقم الفاتورة': invoice.invoiceNumber,
          'التاريخ': formatDate(invoice.date),
          'العميل': invoice.customerName || '-',
          'المورد': invoice.supplierName || '-',
          'الفرع': invoice.branchName || '-',
          'النوع': 'مردودات استيراد',
          'الحالة': invoice.status,
          'الإجمالي': invoice.total
        }))
      ];

      // دمج البيانات حسب نوع التقرير المحدد
      let exportData: any[] = [];
      switch (reportType) {
        case 'sales':
          exportData = salesExportData;
          break;
        case 'returns':
          exportData = returnsExportData;
          break;
        default:
          exportData = [...salesExportData, ...returnsExportData];
      }

      if (exportData.length === 0) {
        setError('لا توجد بيانات للتصدير');
        return;
      }

      // إنشاء ملف Excel
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'تقرير المبيعات');

      // تحميل الملف
      const fileName = `تقرير_المبيعات_${startDate}_${endDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err: any) {
      console.error('Error exporting sales report:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء تصدير التقرير');
    } finally {
      setExporting(false);
    }
  };

  // معالج طباعة التقرير
  const handlePrint = () => {
    window.print();
  };

  // معالج عرض تفاصيل الفاتورة
  const handleViewInvoice = (invoiceId: string, pageId?: string) => {
    // التنقل إلى صفحة عرض الفاتورة - المسار الصحيح: /invoices/{pageId}/{invoiceId}/view
    if (pageId) {
      navigate(`/invoices/${pageId}/${invoiceId}/view`);
    } else {
      console.error('معرف الصفحة غير موجود للفاتورة:', invoiceId);
    }
  };

  // تنسيق العملة
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // عرض التاريخ كما هو من قاعدة البيانات بدون تحويل
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    
    // استخراج التاريخ فقط من النص (إذا كان بصيغة ISO: 2024-01-15T10:30:00.000Z)
    if (dateString.includes('T')) {
      const datePart = dateString.split('T')[0]; // 2024-01-15
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // إذا كان التاريخ بصيغة أخرى، أرجعه كما هو
    return dateString;
  };

  // فلترة البيانات حسب نوع التقرير
  const getFilteredData = () => {
    switch (reportType) {
      case 'sales':
        return { salesInvoices: reportData.salesInvoices, returnInvoices: [] };
      case 'returns':
        return { salesInvoices: [], returnInvoices: reportData.returnInvoices };
      default:
        return { salesInvoices: reportData.salesInvoices, returnInvoices: reportData.returnInvoices };
    }
  };

  // معالج تغيير الفروع المحددة
  const handleBranchChange = (event: any) => {
    const value = event.target.value;
    
    // إذا تم اختيار "الكل"
    if (value.includes('all')) {
      if (selectedBranches.includes('all')) {
        // إذا كان "الكل" محدد مسبقاً، قم بإلغاء تحديده
        setSelectedBranches([]);
      } else {
        // إذا لم يكن "الكل" محدد، حدده وألغ باقي الاختيارات
        setSelectedBranches(['all']);
      }
    } else {
      // إزالة "الكل" من الاختيارات إذا تم اختيار فرع محدد
      const newSelection = value.filter((v: string) => v !== 'all');
      setSelectedBranches(newSelection);
    }
  };

  // الحصول على أسماء الفروع المحددة للعرض
  const getSelectedBranchNames = () => {
    if (selectedBranches.includes('all')) {
      return 'جميع الفروع';
    }
    
    const selectedNames = branches
      .filter(branch => selectedBranches.includes(branch._id))
      .map(branch => branch.name);
    
    return selectedNames.length > 0 ? selectedNames.join(', ') : 'لم يتم تحديد فروع';
  };

  const filteredData = getFilteredData();

  return (
    <Box sx={{ maxWidth: '100%', bgcolor: '#f8f9fa', p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          backgroundImage: 'linear-gradient(to right, #1976d2, #2196f3)',
          color: 'white'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon fontSize="large" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
              تقارير المبيعات والمردودات
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/invoices')}
              sx={{ 
                color: '#1976d2', 
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              العودة
            </Button>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{ 
                color: '#1976d2', 
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              تحديث
            </Button>
            <Button
              variant="contained"
              color="inherit"
              startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting || loading}
              sx={{
                color: '#1976d2',
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              {exporting ? 'جاري التصدير...' : 'تصدير'}
            </Button>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ 
                color: '#1976d2', 
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              طباعة
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* فلاتر التاريخ */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DateRangeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#1976d2' }}>
            فلترة التقارير
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="من تاريخ"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="إلى تاريخ"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>نوع التقرير</InputLabel>
              <Select
                value={reportType}
                onChange={(e) => setReportType(e.target.value as any)}
                label="نوع التقرير"
              >
                <MenuItem value="all">الكل</MenuItem>
                <MenuItem value="sales">المبيعات فقط</MenuItem>
                <MenuItem value="returns">المردودات فقط</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>الفروع</InputLabel>
              <Select
                multiple
                value={selectedBranches}
                onChange={handleBranchChange}
                input={<OutlinedInput label="الفروع" />}
                renderValue={() => getSelectedBranchNames()}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                      width: 250,
                    },
                  },
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={selectedBranches.includes('all')} />
                  <ListItemText primary="جميع الفروع" />
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch._id} value={branch._id}>
                    <Checkbox checked={selectedBranches.includes(branch._id)} />
                    <ListItemText primary={branch.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* بطاقات الملخص */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ borderRadius: 2, borderLeft: '4px solid #4caf50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        إجمالي المبيعات
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {formatCurrency(reportData.totalSales)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        عدد الفواتير: {reportData.salesCount}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, color: '#4caf50', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ borderRadius: 2, borderLeft: '4px solid #f44336' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        إجمالي المردودات
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                        {formatCurrency(reportData.totalReturns)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        عدد المردودات: {reportData.returnsCount}
                      </Typography>
                    </Box>
                    <TrendingDownIcon sx={{ fontSize: 48, color: '#f44336', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ borderRadius: 2, borderLeft: '4px solid #2196f3' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        صافي المبيعات
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                        {formatCurrency(reportData.netSales)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        المبيعات - المردودات
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 48, color: '#2196f3', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* جداول التفاصيل */}
          <Grid container spacing={3}>
            {/* جدول المبيعات */}
            {(reportType === 'all' || reportType === 'sales') && filteredData.salesInvoices.length > 0 && (
              <Grid item xs={12} lg={reportType === 'all' ? 6 : 12}>
                <Card elevation={3} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ReceiptIcon color="success" />
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#4caf50' }}>
                        فواتير المبيعات ({reportData.salesCount})
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell>رقم الفاتورة</TableCell>
                            <TableCell>التاريخ</TableCell>
                            <TableCell>العميل</TableCell>
                            <TableCell>الفرع</TableCell>
                            <TableCell>المبلغ</TableCell>
                            <TableCell>الحالة</TableCell>
                            <TableCell>إجراءات</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredData.salesInvoices
                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                            .map((invoice) => (
                            <TableRow key={invoice._id} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                              <TableCell>{invoice.invoiceNumber}</TableCell>
                              <TableCell>{formatDate(invoice.date)}</TableCell>
                              <TableCell>{invoice.customerName || 'غير محدد'}</TableCell>
                              <TableCell>{invoice.branchName || 'غير محدد'}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                {formatCurrency(invoice.total)}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={invoice.status === 'completed' ? 'مكتملة' : 'مسودة'}
                                  color={invoice.status === 'completed' ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="عرض التفاصيل">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewInvoice(invoice._id, invoice.pageId)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* جدول المردودات */}
            {(reportType === 'all' || reportType === 'returns') && filteredData.returnInvoices.length > 0 && (
              <Grid item xs={12} lg={reportType === 'all' ? 6 : 12}>
                <Card elevation={3} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <AssignmentIcon color="error" />
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#f44336' }}>
                        فواتير المردودات ({reportData.returnsCount})
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell>رقم المردود</TableCell>
                            <TableCell>التاريخ</TableCell>
                            <TableCell>العميل</TableCell>
                            <TableCell>الفرع</TableCell>
                            <TableCell>المبلغ</TableCell>
                            <TableCell>النوع</TableCell>
                            <TableCell>الحالة</TableCell>
                            <TableCell>إجراءات</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredData.returnInvoices
                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                            .map((invoice) => (
                            <TableRow key={invoice._id} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                              <TableCell>{invoice.invoiceNumber}</TableCell>
                              <TableCell>{formatDate(invoice.date)}</TableCell>
                              <TableCell>{invoice.customerName || invoice.supplierName || 'غير محدد'}</TableCell>
                              <TableCell>{invoice.branchName || 'غير محدد'}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: '#f44336' }}>
                                {formatCurrency(invoice.total)}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={invoice.type === 'sales_return' ? 'مرتجع مبيعات' : 'مرتجع استيراد'}
                                  color={invoice.type === 'sales_return' ? 'warning' : 'info'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={invoice.status === 'completed' ? 'مكتملة' : 'مسودة'}
                                  color={invoice.status === 'completed' ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="عرض التفاصيل">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewInvoice(invoice._id, invoice.pageId)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Pagination */}
          {(filteredData.salesInvoices.length > itemsPerPage || filteredData.returnInvoices.length > itemsPerPage) && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(Math.max(filteredData.salesInvoices.length, filteredData.returnInvoices.length) / itemsPerPage)}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SalesReportsPage;